import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AsyncBoundary } from "@/ui/patterns";
import { ApiError, isApiError } from "@/data/api/client";
import { normalizeReservationCode } from "@/domain/reservation/rules";
import { useReservationLookup } from "@/features/validate/hooks/useReservationLookup";
import { CodeLookupForm } from "./CodeLookupForm";
import { ReservationSummary } from "./ReservationSummary";
import { ValidationAnswer } from "./ValidationAnswer";

const codeParam = "kod";

/** A reservation of another business answers the same 404 as a code that was
 *  never issued, and this screen must not tell the two apart. */
function isUnknownCode(error: unknown): boolean {
  return isApiError(error, "CODE_NOT_FOUND") || (error instanceof ApiError && error.status === 404);
}

function lookupProblem(error: unknown): string {
  if (isUnknownCode(error)) {
    return "Takový kód nemáme. Zkontrolujte prosím zadání a zkuste to znovu.";
  }
  return "Kód se teď nepodařilo ověřit. Zkuste to prosím znovu.";
}

function arrivalProblem(error: unknown): string | null {
  if (error === null) return null;
  if (isApiError(error, "NOT_PAID")) {
    return "Rezervace není zaplacená, proto k ní příchod zaznamenat nelze.";
  }
  if (isUnknownCode(error)) {
    return "Tuto rezervaci už nenajdeme. Zadejte prosím kód znovu.";
  }
  return "Příchod se nepodařilo zaznamenat. Zkuste to prosím znovu.";
}

export function CodeValidation() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is where the code enters the screen — reloaded, shared or typed by
  // hand — so spaces and lower case are forgiven here, once, for every path in.
  const code = normalizeReservationCode(searchParams.get(codeParam) ?? "");
  const [codeInField, setCodeInField] = useState(code);
  const { reservation, isLoading, error, searchAgain, recordArrival } = useReservationLookup(code);
  // A malformed code is refused by the form, so the lookup keeps answering the
  // code before it. Hiding that answer is what stops a partner from recording
  // an arrival against the reservation of a code they have typed over.
  const isAnswerForCodeInField = code !== "" && codeInField === code;

  const search = (typedCode: string) => {
    recordArrival.reset();
    const requestedCode = normalizeReservationCode(typedCode);
    // Submitting the same code is how a partner retries after a hiccup, and the
    // URL alone would not change, so the lookup has to be asked again by hand.
    if (requestedCode === code) {
      void searchAgain();
      return;
    }
    setSearchParams({ [codeParam]: requestedCode }, { replace: true });
  };

  return (
    <div className="space-y-8">
      <CodeLookupForm
        code={code}
        onSubmit={search}
        onCodeInFieldChange={setCodeInField}
        isSearching={isLoading}
      />

      {isAnswerForCodeInField && (
        <AsyncBoundary
          data={reservation}
          isLoading={isLoading}
          error={error}
          errorFallback={(lookupError) => (
            <ValidationAnswer
              tone={isUnknownCode(lookupError) ? "unknownCode" : "lookupFailed"}
              headline={isUnknownCode(lookupError) ? "Kód nenalezen" : "Ověření se nezdařilo"}
            >
              <p>{lookupProblem(lookupError)}</p>
              {isUnknownCode(lookupError) && (
                <p>
                  Rezervaci můžete zkusit najít podle jména zákazníka v{" "}
                  <Link
                    to="/reservations"
                    className="underline underline-offset-4 hover:text-foreground"
                  >
                    rezervacích
                  </Link>
                  .
                </p>
              )}
            </ValidationAnswer>
          )}
        >
          {(found) => (
            <ReservationSummary
              reservation={found}
              onRecordArrival={() => recordArrival.mutate(found.id)}
              isRecording={recordArrival.isPending}
              justRecorded={recordArrival.isSuccess}
              problem={arrivalProblem(recordArrival.error)}
            />
          )}
        </AsyncBoundary>
      )}
    </div>
  );
}
